import { ResultsView } from '@kijiji/generated/graphql-types'
import { theme } from '@kijiji/theme'
import dynamic from 'next/dynamic'
import { useSession } from 'next-auth/react'

import { SrpResults } from '@/components/srp/results-sort/results'
import { getAvailableViewOptions } from '@/components/srp/results-sort/sort/getAvailableViewOptions'
import {
  SaveSearchWrapper,
  SrpResultsSortContainer,
  SrpSortContainer,
} from '@/components/srp/results-sort/styled'
import { SaveSearchChipButton } from '@/components/srp/save-search-chip'
import { isUserAuthenticated } from '@/features/auth/constants/user'
import { SRPViewButton } from '@/features/map/components/srp-view-button/SRPViewButton'
import { useGetSrpState } from '@/features/search/hooks/useGetSrpState'
import { useGetLocation } from '@/hooks/location/useGetLocation'
import { useSrpMwebRedesign } from '@/hooks/srp/useSrpMwebRedesign'
import { RemoteParamKeys, useToggle } from '@/lib/firebase/hooks'
import { trackEvent } from '@/lib/ga'
import { GA_SAVE_SEARCH_TYPES } from '@/lib/ga/constants/ga'
import { GA_EVENT } from '@/lib/ga/constants/gaEvent'
import { Flex } from '@/ui/atoms/flex'
import { Spacing } from '@/ui/atoms/spacing'
import { SelectVariant } from '@/ui/molecules/select'
import { capitalizeString } from '@/utils/string'

export type SrpResultsSortProps = {
  isMobile?: boolean
  categoryId: number
}

const SrpSort = dynamic(() => import('./sort').then((mod) => mod.SrpSort), { ssr: false })

export const SrpResultsSort = ({ isMobile = false, categoryId }: SrpResultsSortProps) => {
  const { viewType, updatePageViewType } = useGetSrpState()

  const { location } = useGetLocation()

  /**
   * TOGGLES IN USE:
   * - SRP_CONTROLS_MWEB_REDESIGN
   * - SRP_GRID_VIEW_TOGGLE
   * - SAVED_SEARCH_WITHIN_FILTERS
   */
  const isGridViewEnabled = useToggle(RemoteParamKeys.SRP_GRID_VIEW_TOGGLE)?.enabled
  const srpControlsMwebToggle = useSrpMwebRedesign(true)
  const isSrpControlsMwebToggleEnabled = !!(srpControlsMwebToggle && isMobile)

  const saveSearchWithinFiltersDecision = useToggle(RemoteParamKeys.SAVED_SEARCH_WITHIN_FILTERS)
  const { status: userSessionStatus } = useSession()

  const isSaveSearchBtnEnabled =
    saveSearchWithinFiltersDecision?.enabled && isUserAuthenticated(userSessionStatus)

  const availableViewTypes = getAvailableViewOptions({
    categoryId,
    currentView: viewType,
    isGridViewEnabled,
    location,
  })

  const handleToggleView = (newView: ResultsView) => {
    updatePageViewType(newView)
    handleTrackEventCTA(newView)
  }

  const handleTrackEventCTA = (newView: ResultsView) => {
    const trackingLabel = capitalizeString(newView)
    trackEvent({ action: GA_EVENT.PageViewSwitchSuccess, label: `cta=${trackingLabel}` })
  }

  return (
    <SrpResultsSortContainer
      shouldRenderMapSRP={viewType === ResultsView.Map}
      flexDirection="row"
      isSaveSearchActive={
        !!saveSearchWithinFiltersDecision?.enabled && isUserAuthenticated(userSessionStatus)
      }
      isSrpControlsMwebToggleEnabled={isSrpControlsMwebToggleEnabled}
    >
      {isSaveSearchBtnEnabled && (
        <SaveSearchWrapper justifyContent="flex-start">
          <SaveSearchChipButton
            isPillVariant
            isSrpView
            eventTrackingSource={GA_SAVE_SEARCH_TYPES.btn}
          />
        </SaveSearchWrapper>
      )}

      <SrpResults showOnLargeBreakpoint isMobile={isMobile} />

      <Flex
        flexGrow={1}
        gap={theme.spacing.defaultSmall}
        justifyContent="flex-end"
        alignItems="flex-start"
      >
        {availableViewTypes.map((availableViewType) => {
          return (
            <Spacing
              key={`available-view-type-${availableViewType}`}
              mTop={isSrpControlsMwebToggleEnabled ? '0' : theme.spacing.default}
              large={{ mTop: '0' }}
              {...(isSrpControlsMwebToggleEnabled ? { small: { m: '0.7rem 0' } } : {})}
            >
              <SRPViewButton
                onClick={handleToggleView}
                type={availableViewType}
                isSrpControlsMwebToggleEnabled={isSrpControlsMwebToggleEnabled}
              />
            </Spacing>
          )
        })}

        {viewType !== ResultsView.Map || !isMobile ? (
          <SrpSortContainer
            isRoundedSelect={false}
            isSrpControlsMwebToggleEnabled={isSrpControlsMwebToggleEnabled}
          >
            <SrpSort variant={SelectVariant.ROUNDED} isMobile={isMobile} />
          </SrpSortContainer>
        ) : null}
      </Flex>
    </SrpResultsSortContainer>
  )
}
